import { Button } from '@/components/ui/button';
import { useWebinarLng } from '@/contexts/WebinarLanguageContext';
import type { StripePriceOption } from '@/features/webinar/types';
import { useWebinarT } from '@/hooks/useWebinarT';
import { describeStripePriceOption } from '@/lib/stripePriceOptions';
import { cn } from '@/lib/utils';

interface StripePriceOptionText {
  title: string;
  detail: string | null;
  // Rappel affiché au-dessus du formulaire Stripe, qui n'indique que « 157 € / mois ».
  commitment: string | null;
}

function useStripePriceOptionText() {
  const { t, i18n } = useWebinarT('webinars');
  const locale = useWebinarLng() ?? i18n.language;
  return (option: StripePriceOption, index: number): StripePriceOptionText => {
    const description = describeStripePriceOption(option, locale);
    if (!description.amount) {
      return {
        title: t('live.offerEmbed.priceFallback', { index: index + 1 }),
        detail: null,
        commitment: null,
      };
    }
    if (description.kind === 'oneTime') {
      return {
        title: t('live.offerEmbed.priceOneTime', { amount: description.amount }),
        detail: null,
        commitment: null,
      };
    }
    const interval = t(`live.offerEmbed.priceInterval.${description.interval}`, {
      count: description.intervalCount,
    });
    if (description.kind === 'subscription') {
      return {
        title: `${description.amount} ${interval}`,
        detail: t('live.offerEmbed.priceUntilCanceled'),
        commitment: t('live.offerEmbed.priceUntilCanceled'),
      };
    }
    return {
      title: t('live.offerEmbed.priceInstallments', {
        count: description.count,
        amount: description.amount,
      }),
      detail: description.total
        ? t('live.offerEmbed.priceInstallmentsDetail', { interval, total: description.total })
        : interval,
      commitment: t('live.offerEmbed.priceInstallmentsCommitment', {
        count: description.count,
        total: description.total ?? description.amount,
      }),
    };
  };
}

interface StripePriceChoiceProps {
  offerId: number;
  options: StripePriceOption[];
  selectedPriceId: string;
  onSelect: (priceId: string) => void;
  onContinue: () => void;
  // Couleurs du bouton de l'offre, comme sur la carte qui a ouvert le panneau.
  buttonColor?: string | null;
  buttonTextColor?: string | null;
}

export function StripePriceChoice({
  offerId,
  options,
  selectedPriceId,
  onSelect,
  onContinue,
  buttonColor,
  buttonTextColor,
}: StripePriceChoiceProps) {
  const accentColor = buttonColor ?? '#2c6cff';
  const { t } = useWebinarT('webinars');
  const describe = useStripePriceOptionText();
  return (
    <div className="flex-1 min-h-0 overflow-y-auto rounded-2xl bg-white p-4 text-[#1f1f1f]">
      <p id={`stripe-price-choice-${offerId}`} className="mb-3 text-sm font-semibold">
        {t('live.offerEmbed.chooseOption')}
      </p>
      <div
        role="radiogroup"
        aria-labelledby={`stripe-price-choice-${offerId}`}
        className="flex flex-col gap-2"
      >
        {options.map((option, index) => {
          const text = describe(option, index);
          const selected = option.priceId === selectedPriceId;
          return (
            <label
              key={option.priceId}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
                selected ? 'bg-black/5' : 'border-black/15 hover:bg-black/5',
              )}
              style={
                selected
                  ? { borderColor: accentColor, boxShadow: `inset 0 0 0 1px ${accentColor}` }
                  : undefined
              }
            >
              <input
                type="radio"
                name={`stripe-price-${offerId}`}
                value={option.priceId}
                checked={selected}
                onChange={() => onSelect(option.priceId)}
                className="mt-0.5 size-4 shrink-0"
                style={{ accentColor }}
              />
              <span className="flex min-w-0 flex-col">
                <span className="text-sm font-semibold">{text.title}</span>
                {text.detail && <span className="text-xs text-[#1f1f1f]/70">{text.detail}</span>}
              </span>
            </label>
          );
        })}
      </div>
      <Button
        type="button"
        onClick={onContinue}
        className="mt-4 w-full"
        style={{ backgroundColor: accentColor, color: buttonTextColor ?? '#ffffff' }}
      >
        {t('live.offerEmbed.continue')}
      </Button>
    </div>
  );
}

interface StripePriceSummaryProps {
  option: StripePriceOption;
  index: number;
  isLightTheme: boolean;
  // Absent avec une seule formule : il n'y a rien d'autre à choisir.
  onChange?: () => void;
}

export function StripePriceSummary({
  option,
  index,
  isLightTheme,
  onChange,
}: StripePriceSummaryProps) {
  const { t } = useWebinarT('webinars');
  const text = useStripePriceOptionText()(option, index);
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-xs',
        isLightTheme ? 'bg-black/5 text-[#1f1f1f]' : 'bg-white/10 text-white',
      )}
    >
      <span className="min-w-0">
        <span className="font-semibold">{text.title}</span>
        {text.commitment && <span className="block opacity-80">{text.commitment}</span>}
      </span>
      {onChange && (
        <button
          type="button"
          onClick={onChange}
          className="shrink-0 underline underline-offset-2 cursor-pointer"
        >
          {t('live.offerEmbed.changeOption')}
        </button>
      )}
    </div>
  );
}
